import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Container, Row, Col } from 'styled-bootstrap-grid';
import useTranslation from 'next-translate/useTranslation';
import {
  Comment,
  useCurrentUser,
  useIsReleaseTrack,
  useQueryTrack,
} from '../../api';

import TrackUserInfo from './TrackUserInfo';
import renderMetaTags, {
  createTrackShareImageUrl,
} from '../../utils/renderMetaTags';
import PageTitle from '../common/BrowserPageTitle';
import TrackInfo from './TrackInfo';
import TrackAdditionalInfo from './TrackAdditionalInfo';
import { desktop, NoDesktop } from '../../style/constants';
import NotFound from '../../pages/_error';
import { CenteredLoader } from '../common/Loader';
import { TrackPlayerProvider } from '../Player/PlayerContext';
import TrackUserInfoMobile from './TrackUserInfoMobile';

export interface StateProps {
  userId?: number;
  comments: Comment[];
  isFetchingComments: boolean;
  userIsLoggedIn: boolean;
  currentPage: number;
  lastPageReached: boolean;
  totalComments: number;
}

export interface DispatchProps {
  fetchTrackComments: (page?: number, perPage?: number) => void;
  sendTrackComment: (comment: string) => void;
  deleteComment: (
    commentId: number,
    parent: {
      parentCommentId?: number;
    },
  ) => void;
}

export interface OwnProps {
  trackId: number;
}

type Props = StateProps & DispatchProps & OwnProps;

const Index = ({
  userIsLoggedIn,
  userId,
  sendTrackComment,
  comments,
  deleteComment,
  lastPageReached,
  isFetchingComments,
  totalComments,
  trackId,
  currentPage,
  fetchTrackComments,
}: Props) => {
  const { t } = useTranslation('tracks');
  const { data: track, isFetching } = useQueryTrack(trackId);
  const user = useCurrentUser();
  const showLoadMoreBtn = !isFetchingComments && !lastPageReached;
  const isTrackReleased = useIsReleaseTrack(track?.release_status);

  useEffect(() => {
    if (track) {
      if (isTrackReleased) {
        fetchTrackComments(1, 4);
      } else {
        fetchTrackComments(1);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track]);

  if (!track && !isFetching) {
    return <NotFound />;
  }

  const loadMore = () => {
    if (!lastPageReached && !isFetchingComments) {
      fetchTrackComments(currentPage + 1, isTrackReleased ? 4 : 20);
    }
  };

  if (!track || user.requesting || isFetching) {
    return (
      <ContainerStyled>
        <CenteredLoader />
      </ContainerStyled>
    );
  }

  const pageTitle = [
    [track.user.display_name, track.title].filter(Boolean).join(' - '),
    track?.genre?.name,
  ]
    .filter(Boolean)
    .join(' | ');

  return (
    <TrackPlayerProvider track={track}>
      <ContainerStyled>
        <PageTitle description={track && track.track_desc} noGoogleIndex>
          {pageTitle}
        </PageTitle>
        {track &&
          renderMetaTags({
            'og:title': `${pageTitle} | ${t('tracks:listenOnVirpp') as string}`,
            'og:type': 'music.song',
            'og:url': `https://virpp.com/tracks/${track.id}`,
            'og:image': createTrackShareImageUrl(track.id),
            'og:audio': track.track_url,
            'og:audio:type': 'audio/vnd.facebook.bridge',
            'og:description': `${t('tracks:listenNow') as string}`,
            'og:image:alt': `${t('tracks:trackImageFor') as string} ${
              track.title
            } by ${track.user.display_name} ${t('tracks:onVirpp') as string}`,
            'twitter:title': `${track.user.display_name} - ${track.title} | ${
              track.genre?.name
            } | ${t('tracks:listenOnVirpp') as string}`,
            'twitter:image': createTrackShareImageUrl(track.id),
            'twitter:description': `${t('tracks:listenNow') as string}`,
          })}
        <Row>
          {/* ---------------------- track's owner details with track image component ---------------------- */}
          <Col lg={3}>
            <TrackUserInfo track={track} />
          </Col>
          {/* ----------------------------------- track detail component ----------------------------------- */}
          <Col lg={9}>
            <TrackInfo track={track} />
            <NoDesktop>
              <TrackUserInfoMobile track={track} />
            </NoDesktop>
            {/* --------------------------------------- comment feature -------------------------------------- */}
            <TrackAdditionalInfo
              track={track}
              comments={comments}
              sendTrackComment={sendTrackComment}
              userIsLoggedIn={userIsLoggedIn}
              deleteComment={deleteComment}
              userId={userId}
              showLoadMoreBtn={showLoadMoreBtn}
              loadMore={loadMore}
              fetching={isFetchingComments}
              totalComments={totalComments}
            />
          </Col>
        </Row>
      </ContainerStyled>
    </TrackPlayerProvider>
  );
};

export default Index;

// style col without changing the name of the component

const ContainerStyled = styled(Container)`
  @media ${desktop} {
    padding-top: 28px;
  }
`;
